import { DocumentNode, gql } from '@apollo/client';
import { Box, Stack, StackProps, Text } from '@chakra-ui/react';
import * as React from 'react';

import { TicketedEventRegistrationButton } from '@/components/TicketedEventRegistrationButton';
import { EventStatus } from '@/graphql-types/globalTypes';
import { LockIcon, ShieldIcon, StarIcon } from '@/imports/ui/chakra/feather';

import { EventStuff_EventFragment } from './graphql-types/EventStuff_EventFragment';

import { DeactivatedMessage } from './components/DeactivatedMessage';
import { EventEndedMessage } from './components/EventEndedMessage';
import { ExternalRegistrationMessage } from './components/ExternalRegistrationMessage';
import { HappeningNowMessage } from './components/HappeningNowMessage';
import { NoTicketsMessage } from './components/NoTicketsMessage';
import { NotPublishedMessage } from './components/NotPublishedMessage';
import { ReadOnlyRsvpButton } from './components/ReadOnlyRsvpButton';
import { RegisteredMessage } from './components/RegisteredMessage';
import { RsvpMenu } from './components/RsvpMenu';
import { RsvpMessage } from './components/RsvpMessage';
import { SpecialTooltip } from './components/SpecialTooltip';
import { TicketsAvailableMessage } from './components/TicketsAvailableMessage';
import { ViewConferenceButton } from './components/ViewConferenceButton';
import { ViewSimpleEventButton } from './components/ViewSimpleEventButton';
import { ViewTicketButton } from './components/ViewTicketButton';
import { useEventStuff } from './hooks/useEventStuff';

const EVENT_FRAGMENT = gql`
  fragment EventStuff_EventFragment on EventV2 {
    _id
    ...UseEventStuff_EventFragment
    ...TicketsAvailable_EventFragmet
    ...ReadOnlyRsvpButton_EventFragment
    ...ViewSimpleEvent_EventFragment
    ...TicketedEventRegistrationButton_EventFragment
  }
  ${useEventStuff.eventFragment}
  ${TicketsAvailableMessage.eventFragment}
  ${ReadOnlyRsvpButton.eventFragment}
  ${ViewSimpleEventButton.eventFragment}
  ${TicketedEventRegistrationButton.eventFragment}
`;

export const EventStuff: React.FC<{
  event: EventStuff_EventFragment;
  allowSpecialTooltip: boolean;
  allowConferenceJoin: boolean;
}> & {
  eventFragment: DocumentNode;
} = ({ event, allowSpecialTooltip, allowConferenceJoin }) => {
  const {
    isEventFuture,
    isEventLive,
    isEventPast,
    isEventTicketed,
    isEventOpen,
    isCurrentUserRegistered,
    didCurrentUserRsvp,
    areTicketsAvailableForCurrentUser,
    hasTickets,
    isExternalRegistrationUrlActive,
    status,
    isAgoraMeetEvent,
    hasValidAgoraMeetSettings,
  } = useEventStuff(event);

  const message =
    status === EventStatus.DRAFT ? (
      <NotPublishedMessage />
    ) : status === EventStatus.INACTIVE ? (
      <DeactivatedMessage />
    ) : isEventFuture ? (
      isEventOpen ? (
        <RsvpMessage didCurrentUserRsvp={didCurrentUserRsvp} />
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <RegisteredMessage />
        ) : isExternalRegistrationUrlActive ? (
          <ExternalRegistrationMessage />
        ) : hasTickets ? (
          areTicketsAvailableForCurrentUser ? (
            <TicketsAvailableMessage event={event} />
          ) : (
            <NoTicketsMessage />
          )
        ) : (
          <NoTicketsMessage />
        )
      ) : null
    ) : isEventLive ? (
      <HappeningNowMessage />
    ) : isEventPast ? (
      <EventEndedMessage />
    ) : null;

  // Show warning for invalid Agora Meet configuration
  const agoraMeetWarning = isAgoraMeetEvent && !hasValidAgoraMeetSettings ? (
    <Text color="red.500" fontSize="sm" mt={2}>
      This event has an incomplete Agora Meet configuration. Please contact the event organizer.
    </Text>
  ) : null;

  return (
    <Stack
      direction={{
        base: 'column',
        lg: 'row',
      }}
      bg="gray.50"
      borderRadius="lg"
      p="4"
      spacing="4"
      align="center"
      justify="space-between"
    >
      <Stack
        direction={{
          base: 'column',
          lg: 'row',
        }}
        spacing="4"
        align="center"
      >
        {message}
        {agoraMeetWarning}
      </Stack>

      <EventStuffLite
        event={event}
        allowSpecialTooltip={allowSpecialTooltip}
        allowConferenceJoin={allowConferenceJoin}
        direction={{
          base: 'column',
          xl: 'row',
        }}
        alignItems={{ base: 'stretch' }}
        w={{ base: 'full', lg: 'auto' }}
      />
    </Stack>
  );
};

EventStuff.eventFragment = EVENT_FRAGMENT;

export const EventStuffLite: React.FC<
  {
    event: EventStuff_EventFragment;
    allowSpecialTooltip: boolean;
    allowConferenceJoin: boolean;
  } & StackProps
> & {
  eventFragment: DocumentNode;
} = ({ event, allowSpecialTooltip, allowConferenceJoin, ...stackProps }) => {
  const {
    isEventFuture,
    isEventLive,
    isEventPast,
    isEventTicketed,
    isEventOpen,
    isCurrentUserRegistered,
    areTicketsAvailableForCurrentUser,
    hasCanonicalVideo,
    hasSessionWithCanonicalVideo,
    isConference,
    canCurrentUserJoinEventEarly,
    isCurrentUserSpeaker,
    status,
    isAtLeastPartiallyOnline,
    isExternalRegistrationUrlActive,
    isAgoraMeetEvent,
    hasValidAgoraMeetSettings,
  } = useEventStuff(event);

  const secondary =
    status !== EventStatus.ACTIVE ? null : isEventFuture ? (
      isEventOpen ? (
        <RsvpMenu event={event} />
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <ViewTicketButton eventId={event._id} />
        ) : isExternalRegistrationUrlActive ||
          areTicketsAvailableForCurrentUser ? (
          <TicketedEventRegistrationButton event={event} />
        ) : null
      ) : null
    ) : isEventLive ? (
      isEventOpen ? (
        <ReadOnlyRsvpButton event={event} />
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <ViewTicketButton eventId={event._id} />
        ) : isExternalRegistrationUrlActive ||
          areTicketsAvailableForCurrentUser ? (
          <TicketedEventRegistrationButton event={event} />
        ) : null
      ) : null
    ) : isEventPast ? (
      isEventOpen ? null : isEventTicketed ? (
        isCurrentUserRegistered ? (
          <ViewTicketButton eventId={event._id} />
        ) : (hasCanonicalVideo || hasSessionWithCanonicalVideo) &&
          (isExternalRegistrationUrlActive ||
            areTicketsAvailableForCurrentUser) ? (
          <TicketedEventRegistrationButton
            event={event}
            leftIcon={<LockIcon />}
          >
            Watch Recording{hasSessionWithCanonicalVideo && 's'}
          </TicketedEventRegistrationButton>
        ) : null
      ) : null
    ) : null;

  const primary =
    status !==
    EventStatus.ACTIVE ? null : isEventFuture ? null : isEventLive ? (
      isEventOpen ? (
        isConference ? (
          allowConferenceJoin ? (
            isAtLeastPartiallyOnline && (
              <ViewConferenceButton eventId={event._id} />
            )
          ) : null
        ) : (
          isAtLeastPartiallyOnline && (
            <SpecialTooltip allowSpecialTooltip={allowSpecialTooltip}>
              <ViewSimpleEventButton
                isJoiningWithSpecialPermissions={false}
                event={event}
              />
              {isAgoraMeetEvent && !hasValidAgoraMeetSettings && (
                <Text color="red.500" fontSize="sm">
                  Incomplete Agora Meet configuration
                </Text>
              )}
            </SpecialTooltip>
          )
        )
      ) : isEventTicketed ? (
        isCurrentUserRegistered ? (
          isConference ? (
            allowConferenceJoin ? (
              <ViewConferenceButton eventId={event._id} />
            ) : null
          ) : (
            isAtLeastPartiallyOnline && (
              <SpecialTooltip allowSpecialTooltip={allowSpecialTooltip}>
                <ViewSimpleEventButton
                  isJoiningWithSpecialPermissions={false}
                  event={event}
                />
                {isAgoraMeetEvent && !hasValidAgoraMeetSettings && (
                  <Text color="red.500" fontSize="sm">
                    Incomplete Agora Meet configuration
                  </Text>
                )}
              </SpecialTooltip>
            )
          )
        ) : null
      ) : null
    ) : isEventPast ? (
      isConference ? (
        hasSessionWithCanonicalVideo &&
        (isCurrentUserRegistered || isEventOpen) ? (
          allowConferenceJoin ? (
            <ViewConferenceButton eventId={event._id}>
              Watch Recordings
            </ViewConferenceButton>
          ) : null
        ) : null
      ) : hasCanonicalVideo && (isCurrentUserRegistered || isEventOpen) ? (
        <ViewSimpleEventButton
          isJoiningWithSpecialPermissions={false}
          event={event}
        >
          Watch Recording
        </ViewSimpleEventButton>
      ) : null
    ) : null;

  const special = canCurrentUserJoinEventEarly
    ? isConference
      ? allowConferenceJoin
        ? isAtLeastPartiallyOnline && (
            <ViewConferenceButton
              eventId={event._id}
              colorScheme="gray"
              variant="outline"
            />
          )
        : null
      : isAtLeastPartiallyOnline && (
          <ViewSimpleEventButton
            isJoiningWithSpecialPermissions
            event={event}
            leftIcon={isCurrentUserSpeaker ? <StarIcon /> : <ShieldIcon />}
            colorScheme={
              !isEventLive || !isCurrentUserSpeaker ? 'gray' : undefined
            }
            variant={
              !isEventLive || !isCurrentUserSpeaker ? 'outline' : undefined
            }
          >
            {isEventFuture
              ? 'Preview'
              : isEventPast
              ? 'View Page'
              : !isCurrentUserSpeaker
              ? 'Join as Admin'
              : 'Join Now'}
          </ViewSimpleEventButton>
        )
    : null;

  return (
    <Stack {...stackProps}>
      {secondary}

      {primary ? primary : special}
    </Stack>
  );
};

EventStuffLite.eventFragment = EVENT_FRAGMENT;
